import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, downloadFile, blobErrorDetail, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { channelLabel } from "./ApprovalDialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Link2, ShieldCheck, FileText, Download, Ban, RotateCcw, Pencil } from "lucide-react";
import { fmtDate } from "@/lib/workflow";

const isImage = (d) => (d.content_type || "").startsWith("image/");

function DocumentRow({ d, phase, project, actionById, toggleValidity, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(d.caption || "");
  const [preview, setPreview] = useState(false);
  const url = `${API_BASE}/documents/${d.document_id}/download`;
  const saveCaption = async () => {
    try {
      await api.patch(`/projects/${project.project_id}/documents/${d.document_id}/caption`, { caption });
      setEditing(false); await onChanged(); toast.success(caption ? "Caption saved" : "Caption cleared");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className={`flex items-start gap-3 rounded-md border p-2.5 ${d.valid ? "border-slate-200 bg-white" : "border-red-200 bg-red-50/40 opacity-70"}`} data-testid={`document-row-${d.document_id}`}>
      {isImage(d) ? (
        <button onClick={() => setPreview(true)} className="shrink-0 rounded-md overflow-hidden border border-slate-200 bg-slate-100" title="Preview" data-testid={`document-thumb-${d.document_id}`}>
          <img src={url} alt={d.caption || d.file_name} className="h-14 w-20 object-cover" loading="lazy" />
        </button>
      ) : <FileText className="h-4 w-4 text-slate-400 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{d.file_name}</div>
        {isImage(d) && (editing ? (
          <div className="mt-1 flex gap-1">
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={200} placeholder="Caption shown in reports…" className="h-7 text-xs" autoFocus onKeyDown={(e) => { if (e.key === "Enter") saveCaption(); if (e.key === "Escape") setEditing(false); }} data-testid={`document-caption-input-${d.document_id}`} />
            <Button size="sm" className="h-7 bg-[#2563EB] hover:bg-[#1d4ed8] text-xs" onClick={saveCaption} data-testid={`document-caption-save-${d.document_id}`}>Save</Button>
          </div>
        ) : (
          <button onClick={() => { setCaption(d.caption || ""); setEditing(true); }} className={`mt-0.5 inline-flex items-center gap-1 text-xs ${d.caption ? "text-slate-700" : "text-slate-400 italic"} hover:text-slate-900`} data-testid={`document-caption-${d.document_id}`}>
            <Pencil className="h-3 w-3" />{d.caption || "Add caption"}
          </button>
        ))}
        <div className="text-[11px] text-slate-500">{d.content_type || "file"} · {(d.size / 1024).toFixed(1)} KB · {d.uploaded_by_name} · {fmtDate(d.uploaded_at)}</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {d.links.filter((l) => l.phase_id === phase.phase_id).map((l, i) => (
            <Badge key={i} variant="outline" className="text-[10px]">{l.action_id ? actionById(l.action_id) || "action" : "phase"}</Badge>
          ))}
          {!d.valid && <Badge className="text-[10px] bg-red-100 text-red-700 hover:bg-red-100">Invalidated</Badge>}
        </div>
      </div>
      <button type="button" onClick={async () => { try { await downloadFile(`/documents/${d.document_id}/download`, d.file_name); } catch (e) { toast.error(await blobErrorDetail(e)); } }} className="p-1 text-slate-500 hover:text-slate-900" title="Download" data-testid={`document-download-${d.document_id}`}><Download className="h-4 w-4" /></button>
      <button onClick={() => toggleValidity(d)} className="p-1 text-slate-500 hover:text-red-600" title={d.valid ? "Invalidate" : "Restore"} data-testid={`document-validity-${d.document_id}`}>
        {d.valid ? <Ban className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
      </button>
      {isImage(d) && (
        <Dialog open={preview} onOpenChange={setPreview}>
          <DialogContent className="max-w-3xl" data-testid={`document-preview-${d.document_id}`}>
            <DialogHeader><DialogTitle className="text-base">{d.caption || d.file_name}</DialogTitle><DialogDescription>{d.caption ? d.file_name + " · " : ""}{d.uploaded_by_name} · {fmtDate(d.uploaded_at)}</DialogDescription></DialogHeader>
            <img src={url} alt={d.caption || d.file_name} className="max-h-[70vh] w-full object-contain rounded-md bg-slate-100" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const HINTS = {
  brief: "Upload the customer briefing and record the kick-off.",
  technical_design: "Upload drawings and record the customer's drawing approval.",
  material_research: "Upload material specifications and record approvals.",
  supplier_quotation: "Upload supplier quotations received.",
  prototyping: "Upload prototype photos, reports and record approvals.",
  costing_quotation: "Attach the completed Economic File and record the quotation sent.",
  client_confirmation: "Upload or record the written customer confirmation (email, WhatsApp or document).",
  erp_handover: "Attach the completed Economic File for ERP handover.",
};

export const PhaseEvidence = ({ project, phase, allDocuments, onChanged, attachTarget, setAttachTarget }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [actionId, setActionId] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);

  const target = attachTarget?.action_id || actionId || null;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("phase_id", phase.phase_id);
    if (target) fd.append("action_id", target);
    try {
      await api.post(`/projects/${project.project_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      setAttachTarget(null);
      await onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const link = async (doc) => {
    try {
      await api.post(`/projects/${project.project_id}/documents/${doc.document_id}/link`, { phase_id: phase.phase_id, action_id: target });
      toast.success("Document linked — no re-upload needed");
      setLinkOpen(false);
      setAttachTarget(null);
      await onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleValidity = async (doc) => {
    await api.patch(`/projects/${project.project_id}/documents/${doc.document_id}/validity`, null, { params: { valid: !doc.valid } });
    toast.success(doc.valid ? "Document invalidated — readiness recalculated" : "Document restored");
    await onChanged();
  };

  const linkable = allDocuments.filter((d) => !d.links.some((l) => l.phase_id === phase.phase_id && (l.action_id || null) === target));
  const actionById = (id) => phase.actions.find((a) => a.action_id === id)?.title;

  return (
    <section data-testid="phase-evidence-section">
      <p className="overline">Evidence & documents</p>
      <h3 className="text-base font-semibold text-slate-900">{HINTS[phase.key]}</h3>

      <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Attach to action (optional)</Label>
            <Select value={attachTarget?.action_id || actionId || "phase"} onValueChange={(v) => { setActionId(v === "phase" ? "" : v); setAttachTarget(null); }}>
              <SelectTrigger className="h-9 bg-white" data-testid="evidence-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="phase">Phase level only</SelectItem>
                {phase.actions.map((a) => <SelectItem key={a.action_id} value={a.action_id}>{a.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="evidence-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#2563EB] hover:bg-[#1d4ed8] h-9" data-testid="evidence-upload-btn">
            <Upload className="h-4 w-4 mr-2" />{uploading ? "Uploading…" : "Upload file"}
          </Button>
          <Button variant="outline" className="h-9" onClick={() => setLinkOpen(true)} data-testid="evidence-link-existing-btn"><Link2 className="h-4 w-4 mr-2" />Link existing</Button>
        </div>
        {attachTarget && <p className="mt-2 text-xs text-blue-700">Attaching evidence to: <strong>{attachTarget.title}</strong></p>}
      </div>

      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2">Files ({phase.documents.length})</p>
          {phase.documents.length === 0 ? <p className="text-xs text-slate-500">No files linked to this phase yet.</p> : (
            <div className="space-y-2">
              {phase.documents.map((d) => (
                <DocumentRow key={d.document_id} d={d} phase={phase} project={project} actionById={actionById} toggleValidity={toggleValidity} onChanged={onChanged} />
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2">Recorded approvals ({phase.approvals.length})</p>
          {phase.approvals.length === 0 ? <p className="text-xs text-slate-500">No approvals recorded.</p> : (
            <div className="space-y-2">
              {phase.approvals.map((a) => (
                <div key={a.approval_id} className="rounded-md border border-emerald-200 bg-emerald-50/50 p-2.5" data-testid={`approval-row-${a.approval_id}`}>
                  <div className="text-sm text-slate-900"><ShieldCheck className="inline h-3.5 w-3.5 text-emerald-600 mr-1" />{a.approved_by} <span className="text-slate-500">via {channelLabel(a.channel)}</span> · <span className="font-mono text-xs">{a.approved_on}</span></div>
                  <div className="text-[11px] text-slate-500">{a.action_id ? actionById(a.action_id) : "Phase level"} · recorded by {a.recorded_by_name}{a.document_id && " · attachment linked"}</div>
                  {a.notes && <p className="text-xs text-slate-700 mt-1">{a.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Link an existing project document</DialogTitle><DialogDescription>Reuse a file already uploaded to this project without uploading it again.</DialogDescription></DialogHeader>
          <div className="max-h-80 overflow-auto space-y-1">
            {linkable.length === 0 ? <p className="text-sm text-slate-500">No other documents available.</p> : linkable.map((d) => (
              <button key={d.document_id} onClick={() => link(d)} className="w-full text-left rounded-md border border-slate-200 p-2.5 hover:bg-slate-50" data-testid={`link-doc-${d.document_id}`}>
                <div className="text-sm font-medium text-slate-900">{d.file_name}</div>
                <div className="text-[11px] text-slate-500">{d.links.map((l) => l.phase_name).join(", ")} · {fmtDate(d.uploaded_at)}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

    </section>
  );
};
